import alt from "../alt";
import extend from "lodash/assign";
import PersonActions from "../actions/PersonActions";
import PersonUtil from "../utils/PersonUtil";
import EventActions from "../actions/EventActions";
import AppActions from "../actions/AppActions";
import GlobalNotificationActions from "../actions/GlobalNotificationActions";
import TicketActions from "../actions/TicketActions";
import PassActions from "../actions/PassActions";
import CreditActions from "../actions/CreditActions";
import AppStore from "../stores/AppStore";
import qs from "qs";

import logger from "../logger";

import config from "../config";
import Cookies from "js-cookie";

const SHOULD_PERSIST = "localStorage" in window;
const LS_KEY = "eventive-personState";
let cachedPerson;
try {
  cachedPerson =
    (SHOULD_PERSIST &&
      window.localStorage[LS_KEY] &&
      JSON.parse(window.localStorage[LS_KEY])) ||
    {};
} catch (e) {
  // Catch if unable to access localstorage (e.g. due to DOM Exception 18)
  cachedPerson = {};
}

const query = qs.parse(window.location.search.substring(1));

const COOKIE_KEY =
  TENANT.global_config && TENANT.global_config.cannes_mode
    ? "eventive-cannes-token"
    : TENANT.use_aarp_registration_flow
    ? "eventive-token-aarp"
    : "eventive-token";
const cachedToken = (query && query.__token) || Cookies.get(COOKIE_KEY);

// Restore user context from cache if specificed
if (cachedPerson) {
  logger.setUserContext({
    id: cachedPerson.id,
    email: cachedPerson.details && cachedPerson.details.email
  });
}

class PersonStore {
  constructor() {
    this.bindActions(PersonActions);

    this.state = extend(
      {
        isLoggedIn: false,
        id: undefined,
        token: undefined,
        details: undefined,
        loginSuccess: false,
        loginInProgress: false,
        loginErrorMessage: null,

        forgotSuccess: false,
        forgotInProgress: false,
        forgotErrorMessage: null,

        resetSuccess: false,
        resetInProgress: false,
        resetErrorMessage: null,

        editInProgress: false,
        editErrorMessage: null,

        guestCheckoutActiveEmail: null,

        paymentInfoUpdateInProgress: false,
        paymentInfoUpdateSuccess: false,
        paymentInfoUpdateErrorMessage: null,

        loginSucceededHooks: [],

        targetResourceSafe: false
      },
      cachedToken
        ? {
            isLoggedIn: cachedPerson.isLoggedIn,
            id: cachedPerson.id,
            token: cachedToken,
            details: cachedPerson.details
          }
        : {}
    );

    this.exportPublicMethods({
      isLoggedIn: this.isLoggedIn.bind(this),
      getPersonId: this.getPersonId.bind(this),
      getPersonDetails: this.getPersonDetails.bind(this)
    });
  }

  isLoggedIn() {
    return this.state.isLoggedIn;
  }

  getPersonId() {
    if (this.isLoggedIn()) {
      return this.state.id;
    }

    return undefined;
  }

  getPersonDetails() {
    return this.state.details;
  }

  onSetGuestCheckout(email) {
    this.setState({ guestCheckoutActiveEmail: email });
  }

  onUpdateId(id) {
    this.setState({ id });
    if (id && window.ga) {
      window.ga("set", "&uid", id);
    }
  }

  onUpdateToken(token) {
    this.setState({ token });
  }

  onUpdateDetails(details) {
    this.setState({ details });
  }

  onLogin(credentials) {
    this.setState({
      loginInProgress: true,
      loginErrorMessage: null
    });
    PersonUtil.login(credentials)
      .then(PersonActions.updateWithApiData)
      .catch(PersonActions.loginFailed);
  }

  onRegisterWithFacebook(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authFacebook(credentials, /* shouldRegister */ true)
      .then(data => {
        if (credentials.onAccountExists && !data.is_new_person) {
          data.callback = credentials.onAccountExists;
        }
        PersonActions.updateWithApiData(data);
      })
      .catch(PersonActions.loginFailed);
  }

  onLoginWithCannes(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authCannes(credentials)
      .then(PersonActions.updateWithApiData)
      .catch(PersonActions.loginFailed);
  }

  onLoginWithFacebook(token) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authFacebook({ token }, /* shouldRegister */ false)
      .then(PersonActions.updateWithApiData)
      .catch(PersonActions.loginFailed);
  }

  onLoginWithEventival(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authEventival(credentials)
      .then(data => {
        if (credentials.onAccountExists && !data.is_new_person) {
          data.callback = credentials.onAccountExists;
        }
        PersonActions.updateWithApiData(data);
      })
      .catch(PersonActions.loginFailed);
  }

  onLoginWithIda(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authIda(credentials)
      .then(data => {
        if (credentials.onAccountExists && !data.is_new_person) {
          data.callback = credentials.onAccountExists;
        }
        PersonActions.updateWithApiData(data);
      })
      .catch(PersonActions.loginFailed);
  }

  onLoginWithSundance(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authSundance(credentials)
      .then(data => {
        const originalResponse = Object.assign({}, data);

        if (credentials.callback || credentials.onAccountExists) {
          data.callback = () => {
            if (credentials.callback) {
              credentials.callback(null, originalResponse);
            }
            if (credentials.onAccountExists && !data.is_new_person) {
              credentials.onAccountExists();
            }
          };
        }
        PersonActions.updateWithApiData(data);
      })
      .catch(err => {
        if (credentials.callback) {
          credentials.callback(err);
        } else {
          PersonActions.loginFailed(err);
        }
      });
  }

  onLoginWithAARP(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.authAARP(credentials)
      .then(data => {
        if (credentials.onAccountExists && !data.is_new_person) {
          data.callback = credentials.onAccountExists;
        }
        PersonActions.updateWithApiData(data);
      })
      .catch(PersonActions.loginFailed);
  }

  onLoginSucceeded() {
    this.setState({
      isLoggedIn: true,
      loginInProgress: false,
      loginSuccess: true,
      loginErrorMessage: null
    });

    // Update user context for logging
    logger.setUserContext({
      id: this.state.id,
      email: this.state.details && this.state.details.email
    });

    // Welcome the user
    // Currently disabled, but an option for the future...
    // if (this.state.details && this.state.details.email) {
    //   GlobalNotificationActions.notify.defer(`Welcome to ${TENANT.display_name}, ${this.state.details.name ? this.state.details.name.split(' ')[0] : this.state.details.email}!`);
    // }

    // Update errythang
    TicketActions.fetchAll.defer();
    PassActions.fetchAll.defer({ reset: true });
    EventActions.fetchAll.defer();

    // Call loginSucceeded hooks
    this.state.loginSucceededHooks.forEach(h => {
      h();
    });
    this.setState({
      loginSucceededHooks: []
    });
  }

  onLoginFailed(e) {
    this.setState({
      isLoggedIn: false,
      id: undefined,
      token: undefined,
      details: undefined,
      loginInProgress: false,
      loginSuccess: false,
      loginErrorMessage: e.message
    });
  }

  onLogout() {
    this.setState({
      isLoggedIn: false,
      id: undefined,
      token: undefined,
      details: undefined,
      loginSuccess: false,
      loginInProgress: false,
      loginErrorMessage: null,
      targetResourceSafe: false
    });

    logger.setUserContext();

    CreditActions.reset.defer();
    AppActions.fetchSettings.defer();
    TicketActions.updateTickets.defer({});
    PassActions.updatePasses.defer({});
    EventActions.fetchAll.defer();
  }

  onRegister(credentials) {
    this.setState({
      loginInProgress: true
    });
    PersonUtil.register(credentials)
      .then(PersonActions.updateWithApiData)
      .catch(PersonActions.loginFailed);
  }

  onUpdateWithApiData(data) {
    this.setState({ targetResourceSafe: !!data.target_resource_safe });
    PersonActions.updateId.defer(data.id);
    PersonActions.updateToken.defer(data.token);
    PersonActions.updateDetails.defer(data.details);
    PersonActions.loginSucceeded.defer(); // Same difference
    if (data.callback) {
      setTimeout(() => data.callback(), 0);
    }
  }

  onUpdatePaymentInfo(data) {
    this.setState({
      paymentInfoUpdateInProgress: true,
      paymentInfoUpdateSuccess: false,
      paymentInfoUpdateErrorMessage: null
    });

    PersonUtil.updatePaymentInfo(this.state.token, data)
      .then(result => {
        PersonActions.updateDetails(result.details);
        PersonActions.paymentInfoUpdateSucceeded();

        if (
          window.__EVENTIVE_EVERYWHERE_EDIT_PAYMENT_METHOD_ON_ACTION
        ) {
          window.__EVENTIVE_EVERYWHERE_EDIT_PAYMENT_METHOD_ON_ACTION({
            target: { response: result && result.details && result.details.payment_method_summary },
            action: "payment_updated"
          });
        }

      })
      .catch(PersonActions.paymentInfoUpdateFailed);
  }

  onPaymentInfoUpdateFailed(e) {
    this.setState({
      paymentInfoUpdateInProgress: false,
      paymentInfoUpdateSuccess: false,
      paymentInfoUpdateErrorMessage: e.message
    });
  }

  onPaymentInfoUpdateSucceeded(e) {
    this.setState({
      paymentInfoUpdateInProgress: false,
      paymentInfoUpdateSuccess: true,
      paymentInfoUpdateErrorMessage: null
    });
  }

  onResetPaymentInfoUpdate() {
    this.setState({
      paymentInfoUpdateInProgress: false,
      paymentInfoUpdateSuccess: false,
      paymentInfoUpdateErrorMessage: null
    });
  }

  onEdit(data) {
    this.setState({
      editInProgress: true,
      editErrorMessage: null
    });
    PersonUtil.edit(this.state.token, data)
      .then(result => {
        PersonActions.updateDetails(result.details);
        PersonActions.editSucceeded();
      })
      .catch(PersonActions.editFailed);
  }

  onEditSucceeded() {
    this.setState({
      editInProgress: false,
      editErrorMessage: null
    });
  }

  onEditFailed(e) {
    this.setState({
      editInProgress: false,
      editErrorMessage: e.message
    });
  }

  onForgotPassword(email) {
    this.setState({
      forgotInProgress: true
    });
    PersonUtil.forgotPassword(email)
      .then(PersonActions.forgotSucceeded)
      .catch(PersonActions.forgotFailed);
  }

  onForgotSucceeded() {
    this.setState({
      forgotInProgress: false,
      forgotSuccess: true,
      forgotErrorMessage: null
    });
  }

  onForgotFailed(e) {
    this.setState({
      forgotInProgress: false,
      forgotSuccess: false,
      forgotErrorMessage: e.message
    });
  }

  onResetPassword(data) {
    this.setState({
      resetInProgress: true
    });
    PersonUtil.resetPassword(data.token, data.password)
      .then(PersonActions.resetSucceeded)
      .catch(PersonActions.resetFailed);
  }

  onResetSucceeded() {
    this.setState({
      resetInProgress: false,
      resetSuccess: true,
      resetErrorMessage: null
    });
  }

  onResetFailed(e) {
    this.setState({
      resetInProgress: false,
      resetSuccess: false,
      resetErrorMessage: e.message
    });
  }

  onClearErrors() {
    this.setState({
      loginErrorMessage: null,
      forgotErrorMessage: null,
      resetErrorMessage: null,
      editErrorMessage: null,
      paymentInfoUpdateErrorMessage: null
    });
  }

  onRefreshWithToken(tokenData) {
    let token, callback;
    if (typeof tokenData === "string") {
      token = tokenData;
    } else {
      token = tokenData.token;
      callback = tokenData.callback;
    }

    PersonUtil.fetchSelf(token, /* isInitialRefresh */ true)
      .then(result => {
        PersonActions.updateToken(token);
        PersonActions.updateDetails(result.details);

        if (callback) {
          callback(null, result);
        }

        // For Eventive Everywhere, we mark the person as logged in immediately.
        // In other cases (e.g. hitting a new site for the first time), we leave
        // `isLoggedIn` false until they confirm to continue using the popup.
        //
        // We also allow the use of a query string argument to force this too,
        // currently used by Skeleton Software in their Eventival mobile app to
        // allow a one click "jump into checkout" flow.
        if (
          window.EVENTIVE_IS_EVERYWHERE ||
          window.location.search.indexOf("force_login=1") !== -1 ||
          window.location.search.indexOf("__token") !== -1
        ) {
          this.setState({ isLoggedIn: true });
        }
      })
      .catch(e => {
        if (callback) {
          callback(e);
        }
      });
  }

  onFetchSelf() {
    PersonUtil.fetchSelf(this.state.token, /* isInitialRefresh */ false).then(
      result => {
        PersonActions.updateDetails(result.details);
      }
    );
  }

  onAddOneTimeLoginSucceededHook(data) {
    this.setState({
      loginSucceededHooks: this.state.loginSucceededHooks.concat([data.fn])
    });
  }
}

let store = alt.createStore(PersonStore, "PersonStore");

if (SHOULD_PERSIST) {
  store.listen(state => {
    try {
      // baseDomain for site1.eventive.org = eventive.org. For custom domains, we persist
      // the cookie across all other subdomains on that site. This means a login on sitea.custom.com
      // will automatically persist to siteb.custom.com, but not to random.othersite.com (due
      // to cookie limitations). If the custom domain is custom.com (i.e. no subdomain), just persist
      // across the whole *.custom.com domain as well.
      let domainParts = window.location.hostname.split(".");
      let baseDomain = (
        domainParts.length >= 3 ? domainParts.slice(1) : domainParts
      ).join(".");

      // In Cannes mode, only persist locally (since login won't work on other event buckets).
      if (
        AppStore.getState().settings &&
        AppStore.getState().settings.cannes_mode
      ) {
        baseDomain = window.location.hostname;
      }

      const query = qs.parse(window.location.search.substring(1));

      // if there is a token in the __token query param, do not save a cookie
      if (state.token && !(query && query.__token)) {
        Cookies.set(COOKIE_KEY, state.token, {
          secure:
            TENANT &&
            (TENANT.event_bucket === "62abdc596e17c300592e3e25" || TENANT.event_bucket === "67a0f8057aa6eac072bf986b") && // only sundance staging event bucket
            window.location.protocol === "https:", // only set it if the current protocol is https, that way it won't break for dev
          domain: baseDomain,
          expires: 30
        });
      } else {
        Cookies.remove(COOKIE_KEY, { domain: baseDomain });
      }

      window.localStorage[LS_KEY] = JSON.stringify({
        isLoggedIn: state.isLoggedIn,
        id: state.id,
        details: state.details
      });
    } catch (e) {
      // Persistence failed :(
    }
  });
}

// Populate logged in user data on initial load
if (cachedToken) {
  PersonActions.refreshWithToken(cachedToken);
}

export default store;



// WEBPACK FOOTER //
// ./src/app/stores/PersonStore.js